/**
 * Tipos internos da tela de Saúde.
 *
 * Papel: aqui ficam `PlanoNoFormulario` e `MedicoNoFormulario`, os formatos que
 * o react-hook-form guarda enquanto a pessoa preenche, os dois valores em
 * branco de onde a criação parte, e `EstadoDaFoto`, o que o formulário do plano
 * fará com a foto da carteirinha no "Salvar".
 *
 * Os dois são derivados dos schemas do contrato com `z.input`, e não escritos à
 * mão, porque é a entrada daqueles schemas que o formulário precisa produzir.
 * Um tipo próprio compilaria e passaria a divergir no dia em que um campo
 * mudasse de forma no contrato.
 */
import {
  type ExtensaoDaFotoDaCarteirinha,
  type novoContatoMedicoSchema,
  type novoPlanoSaudeSchema,
} from '@concierge/shared';
import { type z } from 'zod';

/** A apólice como o formulário a guarda, antes de o schema normalizar. */
export type PlanoNoFormulario = z.input<typeof novoPlanoSaudeSchema>;

/** O médico como o formulário o guarda. */
export type MedicoNoFormulario = z.input<typeof novoContatoMedicoSchema>;

/**
 * Uma apólice em branco, como o formulário de criação começa.
 *
 * Todo campo entra como string vazia, e não ausente: os campos mascarados são
 * controlados, e com `undefined` o React reclamaria da troca de não controlado
 * para controlado na primeira tecla. O schema converte o vazio em `null` no
 * envio.
 *
 * `pessoaId` começa vazio de propósito, e o formulário não salva enquanto a
 * escolha não for feita — um padrão faria a apólice nascer atribuída à primeira
 * pessoa da lista sempre que alguém salvasse sem olhar.
 */
export const PLANO_EM_BRANCO: PlanoNoFormulario = {
  pessoaId: '',
  operadora: '',
  plano: '',
  carteirinha: '',
  cns: '',
  ingresso: '',
  validade: '',
  observacao: '',
};

/** Um médico em branco, como o formulário de criação começa. */
export const MEDICO_EM_BRANCO: MedicoNoFormulario = {
  nome: '',
  especialidade: '',
  paciente: '',
  telefone: '',
  email: '',
  endereco: '',
  observacao: '',
};

/** O que fazer com a foto no "Salvar", do ponto de vista do formulário. */
export type EstadoDaFoto =
  | { tipo: 'mantida' }
  | { tipo: 'excluida' }
  | {
      tipo: 'nova';
      /** A imagem em data URL, já validada por `novaFotoDaCarteirinhaSchema`. */
      dataUrl: string;
      extensao: ExtensaoDaFotoDaCarteirinha;
      /** Quando a imagem foi escolhida, de onde sai o dia do nome do arquivo. */
      instante: Date;
      /** O estado a que o descarte da imagem escolhida volta. */
      anterior: 'mantida' | 'excluida';
    };

/** O estado de partida: a foto gravada, se houver, continua como está. */
export const FOTO_MANTIDA: EstadoDaFoto = { tipo: 'mantida' };
